import React from "react";
import TextField from "@mui/material/TextField";
import { createTheme, styled } from "@mui/material/styles";
import { ThemeProvider } from "@emotion/react";
import { Autocomplete, Popper } from "@mui/material";
import { colors } from "../../common/pallette/colors";

// ✅ Styled TextField
const AlwaysFocusedTextField = styled(TextField)(({ theme, disabled }) => ({
  "& .MuiOutlinedInput-root": {
    "& fieldset": { borderColor: colors.black.dark },
    "&:hover fieldset": { borderColor: colors.black.dark },
    "&.Mui-focused fieldset": { borderColor: colors.black.dark },
    cursor: disabled ? 'not-allowed' : 'text'
  },
  "& .MuiFormLabel-root": { color: colors.black.dark },
  "& .MuiInputBase-input": { padding: "10px", cursor: disabled ? 'not-allowed' : 'text' },
}));

// ✅ Theme
const theme = createTheme({
  components: {
    MuiOutlinedInput: {
      styleOverrides: {
        root: { padding: "0px 14px !important" },
        input: {
          color: colors.black.dark,
          borderColor: colors.black.dark,
        },
      },
    },
    MuiInputBase: {
      styleOverrides: { input: { fontSize: "12px" } },
    },
    MuiInputLabel: {
      styleOverrides: { root: { color: colors.black.dark } },
    },
  },
});

const CustomPopper = styled(Popper)({
  "& .MuiAutocomplete-listbox": { fontSize: "14px" },
});

export default function SingleSelectDefault({
  label,
  optionList,
  handleChange,
  placeholder,
  variant,
  value,
  isReadOnly
}: any) {
  // ✅ Derive the displayed value correctly
  const getSelectedOption = () => {
    if (typeof value === "string") {
      // If the value is a string, check if it directly exists in the list
      const match = optionList.find(
        (opt: any) =>
          (typeof opt === "string" && opt === value) ||
          (typeof opt === "object" && opt.name === value)
      );
      return match || value;
    } else if (typeof value === "number") {
      // If the value is an integer, find the object with that id
      const match = optionList.find(
        (opt: any) => typeof opt === "object" && opt.id === value
      );
      return match || null;
    }
    return null;
  };

  const selectedValue = getSelectedOption();

  return (
    <ThemeProvider theme={theme}>
      <Autocomplete
        fullWidth
        freeSolo
        className={isReadOnly ? "fs12 cursorNotAllowed" : "fs12"}
        disabled={isReadOnly}
        value={selectedValue}
        onChange={(_, newValue) => {
          handleChange(newValue);
        }}
        options={optionList}
        getOptionLabel={(option: any) => {
          if (typeof option === "string") return option;
          if (option && option.name) return option.name;
          return "";
        }}
        filterOptions={(options, params) => {
          const filtered = options.filter((option: any) =>
            typeof option === "string"
              ? option.toLowerCase().includes(params.inputValue.toLowerCase())
              : option.name
              ? option.name
                  .toLowerCase()
                  .includes(params.inputValue.toLowerCase())
              : false
          );

          if (params.inputValue !== "") {
            filtered.push({ name: `create '${params.inputValue}'` });
          }
          return filtered;
        }}
        PopperComponent={CustomPopper}
        renderInput={(params) => (
          <AlwaysFocusedTextField
            focused
            {...params}
            label={label || ""}
            placeholder={placeholder || "Search"}
            className="fs12"
            variant={variant || "outlined"}
            disabled={isReadOnly}
            InputProps={{
              ...params.InputProps,
              type: "search",
            }}
          />
        )}
      />
    </ThemeProvider>
  );
}
